<script setup lang="ts">
import { computed, onActivated, onDeactivated, useTemplateRef, watch } from "vue";

import { loadBoard } from "@/backend/read/BackendReader";
import { boardScaleFromWindow } from "@/math/coordinate-transform";
import {
  baselineFontPx,
  baselineWidth,
  boardAspect,
  fakeZoom,
  verticalBaselineHeight,
} from "@/model/Settings";
import { useArtObjectivesPanelStore } from "@/store/artObjectivesPanel";
import { useBoardSizeStore } from "@/store/boardSize";
import { useBoardsStore } from "@/store/boards";
import { useTeamStore } from "@/store/team";
import { blurHandlerElem } from "@/utils/dom/dom";

import TeamObjectivesColumn from "./internal/artObjectives/TeamObjectivesColumn.vue";
import { MAX_CELL_HEIGHT, teamHeaderScale } from "./internal/artObjectives/computeLayout";
import { computeNoWrapLayout } from "./internal/artObjectives/computeNoWrapLayout";
import { useFluidBoard } from "./internal/useFluidBoard";

const rootRef = useTemplateRef("root");

const boardsStore = useBoardsStore();
const teamStore = useTeamStore();
const boardSizeStore = useBoardSizeStore();
const artObjectivesPanelStore = useArtObjectivesPanelStore();

const board = computed(() => boardsStore.boardByType("objectives"));
const { focusAndBlur } = useFluidBoard(board, rootRef);

defineExpose(focusAndBlur);

onActivated(() => {
  artObjectivesPanelStore.restore();
  void ensureRelatedBoardsLoaded();
});
onDeactivated(() => {
  artObjectivesPanelStore.close(false);
  artObjectivesPanelStore.unpin();
});

const teams = computed(() => teamStore.teamsInCurrentArt);

/**
 * Reload any related board still stuck in "inited". Router-level
 * `loadOtherBoards` skips boards past that state, so per-team boards (for the
 * columns) and the ART program board (read by the ArtObjectivesPanel) can render
 * empty after an ART switch until reloaded here.
 */
async function ensureRelatedBoardsLoaded() {
  const boards = [
    boardsStore.boardByType("program"),
    ...teams.value.map((team) => boardsStore.boardByType("team", { teamId: team.id })),
  ];
  for (const b of boards) {
    if (b.state.state === "inited") {
      await loadBoard(b, { subscribe: false, skipStickies: true, skipIterations: true });
    }
  }
}

watch(
  () => board.value.id,
  () => {
    artObjectivesPanelStore.unpin();
    void ensureRelatedBoardsLoaded();
  },
);

const teamCounts = computed(() =>
  teams.value.map((team) => {
    const b = boardsStore.boardByType("team", { teamId: team.id });
    return { committed: b.objectives.length, uncommitted: b.stretchObjectives.length };
  }),
);

const boardSize = computed(() => boardScaleFromWindow(boardSizeStore.board.size));

/**
 * Baseline-px-per-window-px scale. Wide windows (aspect ≥ 16:9) lock on height,
 * narrow windows on width; both formulas converge at the 16:9 boundary so the
 * layout doesn't jump when the aspect crosses it.
 */
function baselineScale(): number {
  const W = window.innerWidth;
  const H = window.innerHeight;
  return W / H >= boardAspect ? verticalBaselineHeight / H : baselineWidth / W;
}

const layout = computed(() => {
  const scale = baselineScale();
  return computeNoWrapLayout({
    boardWidth: boardSize.value.x * scale,
    boardHeight: boardSize.value.y * scale,
    teams: teamCounts.value,
  });
});

// `--cell-height` is em-equivalent of `board.len(MAX_CELL_HEIGHT px)` so the
// cell box scales with the same font-size cascade as its em-based content.
// `--art-scale` cascades through .columns' font-size and compacts everything
// proportionally when the packer picks a smaller cellH.
const MAX_CELL_EM = (MAX_CELL_HEIGHT * fakeZoom) / baselineFontPx;

const gridStyle = computed(() => ({
  "--cell-height": `${MAX_CELL_EM}em`,
  "--art-scale": String(layout.value.scale),
  "--art-header-scale": String(teamHeaderScale(layout.value.scale)),
  "--n-columns": String(layout.value.columns.length),
}));
</script>

<template>
  <!-- eslint-disable-next-line vuejs-accessibility/click-events-have-key-events vuejs-accessibility/no-static-element-interactions-->
  <div
    :id="board.id"
    ref="root"
    class="board board-art-objectives"
    :aria-label="$t('label.objective.board')"
    tabindex="0"
    @click="blurHandlerElem"
  >
    <div class="backdrop">
      <div class="columns" :style="gridStyle">
        <!-- Padded to the packer's chosen column count; trailing empties render
             as blank white columns so the grey backdrop doesn't show through. -->
        <div
          v-for="(column, colIdx) in layout.columns"
          :key="colIdx"
          class="column"
          :class="{ 'has-right-divider': colIdx < layout.columns.length - 1 }"
        >
          <div
            v-for="(teamIdx, rowIdx) in column"
            :key="teamIdx"
            class="team-slot"
            :class="{ 'has-bottom-divider': rowIdx < column.length - 1 }"
          >
            <TeamObjectivesColumn :team="teams[teamIdx]" />
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<style scoped lang="scss">
@use "@/styles/board";
@use "@/styles/mixins/a11y";
@use "@/styles/variables/colors";
@use "@/styles/variables/a11y" as colors-a11y;

.board {
  @include a11y.board;

  background: colors.$background-grey;
}

// Column count is chosen by the packer (variable K in [K_MIN..K_MAX]) and supplied as `--n-columns`.
.columns {
  display: grid;
  grid-template-columns: repeat(var(--n-columns), minmax(0, 1fr));
  align-items: stretch;
  height: 100%;
  width: 100%;
  box-sizing: border-box;
  font-size: calc(1em * var(--art-scale, 1));
}

.column {
  display: flex;
  flex-direction: column;
  min-width: 0;

  // Empty space below the last team-slot reads as a continuation of the team
  // area rather than the board's grey backdrop showing through.
  background-color: colors.$background-ui;
  max-height: 100%;
  min-height: 0;
  overflow-y: auto;

  &.has-right-divider {
    border-right: board.len(1px) solid colors-a11y.$divider-color;
  }
}

// Vertical padding only; horizontal insets live on individual children so
// dividers span the full column width.
.team-slot {
  padding: board.len(20px) 0;

  &.has-bottom-divider {
    border-bottom: board.len(1px) solid colors-a11y.$divider-color;
  }
}
</style>
